/**
 * ¿Qué cuentas del informe cuentan CERO en las métricas de eventos porque nadie eligió qué evento
 * medir? (card UvIR4eHe, reunión del 17/09).
 *
 * El Explorador lo preguntaba antes de cada consulta y abría un modal que se imponía
 * (`DataExplorerPage.handleExplore`). Informes nunca lo preguntó: con la semilla en la familia de
 * eventos (card RsuG69eR), una cuenta de Meta sin eventos elegidos mostraba cero sin explicación.
 * Acá vive la parte pura; el pedido está en `useEventsCheck`, y lo que se ve, en `EventsNotice` (arriba
 * del folio) y en el ícono de cada bloque (`PanelEventsWarning`).
 *
 * La fuente es `GET client/<id>/ad-accounts/events-check` (`needs_events_configuration` en el
 * backend), que devuelve las cuentas de canales SIN conversiones primarias, fuera de ecommerce, que
 * nunca eligieron eventos. En Google, TikTok y LinkedIn —que SÍ tienen primarias— no hay nada que
 * avisar: sin eventos elegidos, «Eventos seleccionados» son las primarias.
 *
 * Hasta el 2026-09-27 esa lista traía de más (review C-INFCONV-01): DV360, Search Console y
 * Klaviyo, que tampoco tienen primarias pero NO admiten elegir eventos. Avisarlas dejaba un aviso
 * permanente que el modal no puede resolver. El backend ya las saca en la fuente (`has_custom_actions`
 * en `needs_events_configuration`), lo que arregla también el chat y el wizard. El recorte de acá a
 * canales con `hasActions` —el mismo criterio del panel del cliente, `accountHasNoEvents` en
 * `panel_v11/conversionLabels.js`— queda como segunda línea: un canal nuevo que el front no conoce
 * no genera un aviso sin salida.
 */

import {API_URL} from '../../../settings/local_settings';
import {CHANNEL_INFO_BY_CHANNEL} from '../../channels/channels';
import {accountItemId, SOURCE_AD_ACCOUNTS, SOURCE_BUSINESS_UNITS} from '../settings/AccountsScopeSelector';
import {PANEL_TYPES, tableColumnsOf} from './informeModel';

/** Las métricas que dependen de eventos elegidos, según el catálogo (`EVENT_DEPENDENT_FIELDS`). */
export function eventDependentFieldsOf(fieldsData) {
    return (fieldsData && fieldsData.EVENT_DEPENDENT_FIELDS) || [];
}

/**
 * ¿Este bloque MUESTRA alguna métrica de eventos? Una tabla sin recorte propio muestra la paleta
 * entera: por eso va por `tableColumnsOf` y no por `panel.metrics`.
 */
export function panelUsesEventMetrics(panel, fields, eventFields) {
    if (!panel || !eventFields || !eventFields.length) return false;
    const metrics = panel.type === PANEL_TYPES.TABLE
        ? tableColumnsOf(panel, fields).metrics
        : (panel.metrics || []);
    return metrics.some((m) => eventFields.includes(m));
}

/**
 * ¿Algún bloque del informe muestra métricas de eventos? Se mide sobre los PANELES y no sobre la
 * paleta: una métrica que está en la paleta pero no se muestra en ningún lado no pinta ningún cero.
 */
export function templateUsesEventMetrics(template, eventFields) {
    if (!template) return false;
    return (template.panels || []).some((p) => panelUsesEventMetrics(p, template.fields, eventFields));
}

/** Las líneas de control ACTIVAS del cliente, según el form-data (`business_units`). */
function clientBusinessUnitIds(businessUnits, agencyClientId) {
    return (businessUnits || [])
        .filter((bu) => String(bu.agency_client_id) === String(agencyClientId))
        .map((bu) => String(bu.id));
}

/**
 * La URL del chequeo para el alcance del informe, o `null` si no hay nada que chequear.
 *
 * Con líneas de control, el backend recorta a sus cuentas (`business_unit_ids`). «Todas las
 * líneas» (selección vacía) manda TODAS las del cliente: sin el parámetro, el backend mira todas
 * las cuentas del cliente, incluidas las que no están en ninguna línea y que el informe no muestra
 * (review C-INFCONV-03). Un cliente sin líneas no tiene nada que avisar.
 *
 * Con cuentas elegidas se recorta acá (`unconfiguredInScope`): el endpoint no sabe filtrar por
 * cuenta.
 */
export function eventsCheckUrl(agencyClientId, settings, businessUnits = []) {
    const base = `${API_URL}client/${agencyClientId}/ad-accounts/events-check`;
    if (!settings || settings.source_type !== SOURCE_BUSINESS_UNITS) return base;
    const selected = settings.selected_items || [];
    const ids = selected.length ? selected : clientBusinessUnitIds(businessUnits, agencyClientId);
    if (!ids.length) return null;
    return `${base}?business_unit_ids=${ids.map(encodeURIComponent).join(',')}`;
}

/** ¿El canal deja elegir eventos? Si no, avisar de una cuenta suya es un aviso sin salida. */
function acceptsEvents(channel) {
    const info = CHANNEL_INFO_BY_CHANNEL[channel];
    return !!(info && info.hasActions);
}

/**
 * Las cuentas sin eventos que caen DENTRO del informe y que se PUEDEN configurar. En
 * `selected_items` una cuenta se nombra `"{canal}__{account_id}"` (id de la plataforma) y el
 * endpoint devuelve `ad_account_db_id` (id de Dashbo): el puente es `adAccounts` del form-data,
 * que trae los dos (`id` y `account_id`). Selección vacía = «Incluir todas».
 */
export function unconfiguredInScope(unconfigured, settings, adAccounts) {
    const list = (unconfigured || []).filter((account) => acceptsEvents(account.channel));
    const selected = (settings && settings.selected_items) || [];
    const sourceType = (settings && settings.source_type) || SOURCE_AD_ACCOUNTS;
    if (sourceType !== SOURCE_AD_ACCOUNTS || !selected.length) return list;
    const inScope = new Set(
        (adAccounts || [])
            .filter((aa) => selected.includes(accountItemId(aa.channel, aa.account_id)))
            .map((aa) => String(aa.id)),
    );
    return list.filter((account) => inScope.has(String(account.ad_account_db_id)));
}
